import { useAtom, useAtomValue, useSetAtom, useStore, type PrimitiveAtom } from "jotai";
import {
    changeDraftStepTypeAtom,
    draftStepAtom,
    isCreatingStepAtom,
    removeStepAtom,
    stepIndexBeingEditedAtom,
    upsertDraftStepIntoStepsAtom,
} from "@/routes/automations/automation-details-page/state";
import type React from "react";
import { forwardRef, type ComponentProps } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Select, SelectItem, SelectContent, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import type { WorkflowEditFormStepType } from "@/components/automations/automation-edit-form-data";
import { IconConsoleSimple } from "central-icons/IconConsoleSimple";
import IconBubbleText6 from "central-icons/IconBubbleText6";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { ShellInput } from "@/components/flexkit/ShellInput";
import { useEffect, useRef, useState, type FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { IconPullRequest } from "central-icons/IconPullRequest";
import IconChecklist from "central-icons/IconChecklist";
import type { DraftStep } from "@/routes/automations/automation-details-page/state";
import { AutomationPullRequestStepEditor } from "@/routes/automations/automation-edit-page/AutomationPullRequestStepEditor";
import { AutomationReportStepEditor } from "@/routes/automations/automation-edit-page/AutomationReportStepEditor";
import { useFlagAutomationsStepReportEnabled } from "@/hooks/use-flag";
import { cn } from "@/components/podkit/lib/cn";

type AutomationStepEditAreaProps = ComponentProps<"div"> & {
    onClose: () => void;
};

export const AutomationStepEditArea = forwardRef<HTMLDivElement, AutomationStepEditAreaProps>(
    ({ className, onClose, ...props }, ref) => {
        const draftStep = useAtomValue(draftStepAtom);
        const isCreatingStep = useAtomValue(isCreatingStepAtom);
        const isVisible = draftStep !== null;

        return (
            <div
                ref={ref}
                className={cn(
                    "relative flex max-w-2xl flex-1 grow flex-col overflow-auto border-l border-border-subtle",
                    className,
                )}
                {...props}
            >
                {isVisible ? (
                    <>
                        <Text className="p-6 text-lg font-medium">{isCreatingStep ? "Add Step" : "Edit Step"}</Text>
                        {draftStep && (
                            <AutomationStepEditor create={isCreatingStep} draft={draftStep} onClose={onClose} />
                        )}
                    </>
                ) : null}
            </div>
        );
    },
);
AutomationStepEditArea.displayName = "AutomationStepEditArea";

const AutomationStepEditor: FC<{ create: boolean; draft: DraftStep; onClose: () => void }> = ({
    create,
    draft,
    onClose,
}) => {
    const removeStep = useSetAtom(removeStepAtom);
    const store = useStore();
    const upsertDraftStepIntoSteps = useSetAtom(upsertDraftStepIntoStepsAtom);

    const saveEditorChanges = () => {
        upsertDraftStepIntoSteps();
    };

    const cancelEditorChanges = () => {
        onClose();
    };

    const removeCurrentStep = () => {
        const index = store.get(stepIndexBeingEditedAtom);
        if (index !== null) {
            removeStep(index);
        }
    };

    let contentEditor: React.ReactNode | null;
    switch (draft.type) {
        case "command":
            contentEditor = <StepCommandEditor contentAtom={draft.content} />;
            break;
        case "prompt":
            contentEditor = <StepPromptEditor contentAtom={draft.content} />;
            break;
        case "pr":
            contentEditor = <AutomationPullRequestStepEditor draft={draft} />;
            break;
        case "report":
            contentEditor = <AutomationReportStepEditor draft={draft} />;
            break;
    }

    return (
        <div className="flex flex-1 flex-col gap-3 bg-surface-primary">
            <StepTypeSelect draft={draft} />
            <div className="flex-1">{contentEditor}</div>
            <div className="flex justify-end gap-2 border-t border-border-subtle py-4 pl-4 pr-6">
                {create ? null : (
                    <div className="flex-1">
                        <Button variant="ghost" onClick={removeCurrentStep} data-tracking-id="remove-step">
                            Remove step
                        </Button>
                    </div>
                )}
                <Button variant="outline" onClick={cancelEditorChanges} data-tracking-id="cancel">
                    Cancel
                </Button>
                <Button variant="primary" onClick={saveEditorChanges} data-tracking-id="update">
                    {create ? "Add" : "Update"}
                </Button>
            </div>
        </div>
    );
};

const StepTypeSelect: FC<{ draft: DraftStep }> = ({ draft }) => {
    const changeDraftStepType = useSetAtom(changeDraftStepTypeAtom);
    const { value: reportStepEnabled } = useFlagAutomationsStepReportEnabled();

    let icon: React.ReactNode | null = null;
    let label: string | null = null;

    switch (draft.type) {
        case "command":
            icon = <IconConsoleSimple size={16} />;
            label = "Shell script";
            break;
        case "prompt":
            icon = <IconBubbleText6 size={16} />;
            label = "Prompt";
            break;
        case "pr":
            icon = <IconPullRequest size={16} />;
            label = "Pull request";
            break;
        case "report":
            icon = <IconChecklist size={16} />;
            label = "Report";
            break;
    }

    const updateStepType = (type: WorkflowEditFormStepType) => {
        changeDraftStepType(type);
    };

    return (
        <Select value={draft.type} onValueChange={(value) => updateStepType(value as WorkflowEditFormStepType)}>
            <SelectTrigger loading={false} className="mx-6 w-fit">
                <SelectValue placeholder="Select step type">
                    <div className="flex items-center gap-2">
                        {icon}
                        <span>{label}</span>
                    </div>
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="command">
                    <div className="flex items-center gap-2 text-base">
                        <IconConsoleSimple size={16} />
                        <span>Shell script</span>
                    </div>
                </SelectItem>
                <SelectItem value="prompt">
                    <div className="flex items-center gap-2 text-base">
                        <IconBubbleText6 size={16} />
                        <span>Prompt</span>
                    </div>
                </SelectItem>
                <SelectItem value="pr">
                    <div className="flex items-center gap-2 text-base">
                        <IconPullRequest size={16} />
                        <span>Pull request</span>
                    </div>
                </SelectItem>
                {reportStepEnabled && (
                    <SelectItem value="report">
                        <div className="flex items-center gap-2 text-base">
                            <IconChecklist size={16} />
                            <span>Report</span>
                        </div>
                    </SelectItem>
                )}
            </SelectContent>
        </Select>
    );
};

const StepPromptEditor: FC<{ contentAtom: PrimitiveAtom<string> }> = ({ contentAtom }) => {
    const [value, setValue] = useAtom(contentAtom);
    return (
        <div className="mx-6 h-full max-h-full rounded-lg bg-surface-muted p-1.5">
            <Textarea
                dontAutosize
                className="h-full min-h-40 resize-none rounded-md border border-border-base bg-surface-primary p-3 text-base shadow-sm outline-none focus:border-border-strong focus:ring-0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter the prompt for this step"
            />
        </div>
    );
};

const StepCommandEditor: FC<{ contentAtom: PrimitiveAtom<string> }> = ({ contentAtom }) => {
    const [value, setValue] = useAtom(contentAtom);
    const [availableHeight, setAvailableHeight] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current) {
            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setAvailableHeight(entry.contentRect.height);
                }
            });
            observer.observe(containerRef.current);
            return () => observer.disconnect();
        }
    }, [containerRef]);

    return (
        <div ref={containerRef} className="h-full max-h-full shrink px-6">
            {availableHeight && (
                <ShellInput
                    className="h-full max-h-none"
                    value={value}
                    onChange={setValue}
                    placeholder="Enter shell commands"
                    minHeight={120}
                    maxHeight={availableHeight}
                />
            )}
        </div>
    );
};
